import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  UserAdminView,
  UserCreateRequest,
  UserDetail,
  UserOption,
  UserUpdateRequest,
} from '@kominiarz/shared';
import { api } from './api';
import { queryClient } from './queryClient';

export const USERS_KEY = ['users'] as const;

/** Manager's list of all accounts. */
export function useAdminUsers() {
  return useQuery({
    queryKey: [...USERS_KEY, 'admin'],
    queryFn: async () => (await api<{ users: UserAdminView[] }>('/users')).users,
  });
}

/** Active colleagues (names and colours) for pickers; available to everyone. */
export function useUserOptions() {
  return useQuery({
    queryKey: [...USERS_KEY, 'options'],
    queryFn: async () => (await api<{ users: UserOption[] }>('/users')).users,
    staleTime: 5 * 60_000,
  });
}

export function useUserDetail(id: number) {
  return useQuery({
    queryKey: [...USERS_KEY, 'detail', id],
    queryFn: async () => (await api<{ user: UserDetail }>(`/users/${id}`)).user,
  });
}

const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: USERS_KEY });

export function useCreateUser() {
  return useMutation({
    mutationFn: (body: UserCreateRequest) => api<{ user: UserAdminView }>('/users', { method: 'POST', body }),
    onSuccess: invalidateUsers,
  });
}

export function useUpdateUser(id: number) {
  return useMutation({
    mutationFn: (body: UserUpdateRequest) => api<{ user: UserAdminView }>(`/users/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidateUsers,
  });
}

export function useResetPassword(id: number) {
  return useMutation({
    mutationFn: (password: string) =>
      api<{ user: UserAdminView }>(`/users/${id}/password`, { method: 'POST', body: { password } }),
    onSuccess: invalidateUsers,
  });
}

export function useSetActive(id: number) {
  return useMutation({
    mutationFn: (active: boolean) =>
      api<{ user: UserAdminView }>(`/users/${id}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' }),
    onSuccess: invalidateUsers,
  });
}
